import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatEURDecimal } from "../lib/format";
import { toast } from "sonner";
import { ArrowLeft, Download, FileText, Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const eur = (v) => formatEURDecimal(v);

export default function EmpleadoFicha() {
  const { id } = useParams();
  const currYear = new Date().getFullYear();
  const [year, setYear] = useState(currYear);
  const [compare, setCompare] = useState(currYear - 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/empleados/${id}/ficha?year=${year}&compare=${compare}`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Error cargando ficha"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, year, compare]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return MESES.map((m, i) => {
      const c = data.months_current[i] || {};
      const p = data.months_compare[i] || {};
      return {
        mes: m,
        [`Bruto ${year}`]: c.bruto || 0,
        [`Bruto ${compare}`]: p.bruto || 0,
        [`Coste ${year}`]: c.coste_empresa || 0,
        [`Coste ${compare}`]: p.coste_empresa || 0,
        [`Neto ${year}`]: c.neto || 0,
      };
    });
  }, [data, year, compare]);

  const historicoData = useMemo(() => {
    if (!data) return [];
    return (data.historico_anual || []).map(h => ({
      year: String(h.year),
      Bruto: h.bruto, Neto: h.neto, Coste: h.coste_empresa,
    }));
  }, [data]);

  const downloadZip = async () => {
    setDownloadingZip(true);
    try {
      const r = await api.get(`/empleados/${id}/nominas-zip?year=${year}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `nominas_${(data?.empleado?.nombre || "empleado").replace(/ /g,"_")}_${year}.zip`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error descargando ZIP"); }
    finally { setDownloadingZip(false); }
  };

  const downloadPdf = async (nom_id) => {
    try {
      const r = await api.get(`/nominas/${nom_id}/pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error("Sin PDF asociado"); }
  };

  if (loading && !data) return <div className="p-8 text-center text-sm text-[var(--ink-muted)]"><Loader2 className="inline animate-spin mr-2" size={14}/>Cargando ficha…</div>;
  if (!data) return null;
  const emp = data.empleado;
  const tc = data.totals_current;
  const tp = data.totals_compare;
  const g = data.growth_pct || {};

  return (
    <div className="p-6 max-w-[1500px] mx-auto" data-testid="empleado-ficha-page">
      <Link to="/nominas" className="inline-flex items-center gap-1 text-xs text-[var(--ink-muted)] hover:text-[var(--ink)] mb-3"><ArrowLeft size={12}/> Volver a nóminas</Link>

      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">RRHH · Ficha empleado</p>
          <h1 className="font-display text-4xl font-black tracking-tight" data-testid="ficha-nombre">{emp.nombre}</h1>
          <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--ink-muted)] mt-1">
            {emp.puesto && <span>{emp.puesto}</span>}
            {emp.dni && <span className="font-mono-num">DNI: {emp.dni}</span>}
            {emp.fecha_alta && <span>Alta: {emp.fecha_alta}</span>}
            <span className={`px-2 py-0.5 rounded-full ${emp.activo?"bg-emerald-100 text-emerald-800":"bg-gray-200 text-gray-600"}`}>{emp.activo?"Activo":"Baja"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-[var(--ink-muted)]">Año</label>
          <select value={year} onChange={e=>setYear(parseInt(e.target.value))} className="border rounded px-3 py-2 text-sm" data-testid="ficha-year">
            {[...new Set([...(data.years_available||[]), currYear, currYear-1, currYear+1, year, compare])].sort((a,b)=>b-a).map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <label className="text-xs text-[var(--ink-muted)]">vs</label>
          <select value={compare} onChange={e=>setCompare(parseInt(e.target.value))} className="border rounded px-3 py-2 text-sm" data-testid="ficha-compare">
            {[...new Set([...(data.years_available||[]), currYear-1, currYear-2, currYear, compare])].sort((a,b)=>b-a).map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="ficha-zip" onClick={downloadZip} disabled={downloadingZip} className="inline-flex items-center gap-2 bg-black text-white text-sm px-3 py-2 rounded disabled:opacity-40">
            {downloadingZip ? <Loader2 size={14} className="animate-spin"/> : <Download size={14}/>} Descargar ZIP {year}
          </button>
        </div>
      </div>

      {/* KPI cards con variación YoY */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="Bruto anual" value={eur(tc.bruto)} compareValue={eur(tp.bruto)} compareYear={compare} growth={g.bruto} color="ink"/>
        <KpiCard label="Neto anual" value={eur(tc.neto)} compareValue={eur(tp.neto)} compareYear={compare} growth={g.neto} color="emerald"/>
        <KpiCard label="Coste empresa" value={eur(tc.coste_empresa)} compareValue={eur(tp.coste_empresa)} compareYear={compare} growth={g.coste_empresa} color="rose"/>
        <KpiCard label="Meses cotizados" value={tc.n_meses} compareValue={tp.n_meses} compareYear={compare} growth={null} color="ink"/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KpiCard label="IRPF anual" value={eur(tc.irpf)} compareValue={eur(tp.irpf)} compareYear={compare} growth={g.irpf} color="ink"/>
        <KpiCard label="SS Trabajador" value={eur(tc.ss_trabajador)} compareValue={eur(tp.ss_trabajador)} compareYear={compare} growth={null} color="ink"/>
        <KpiCard label="SS Empresa" value={eur(tc.ss_empresa)} compareValue={eur(tp.ss_empresa)} compareYear={compare} growth={g.ss_empresa} color="ink"/>
        <KpiCard label="Bruto medio/mes" value={eur(tc.n_meses ? tc.bruto/tc.n_meses : 0)} compareValue={eur(tp.n_meses ? tp.bruto/tp.n_meses : 0)} compareYear={compare} growth={null} color="ink"/>
      </div>

      {/* Evolución mensual */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <h3 className="font-display text-lg font-black mb-4">Evolución mensual · Bruto y Coste empresa ({year} vs {compare})</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="mes" fontSize={10}/>
            <YAxis fontSize={10} tickFormatter={v=>new Intl.NumberFormat("es-ES",{notation:"compact"}).format(v)}/>
            <Tooltip formatter={v=>eur(v)}/>
            <Legend wrapperStyle={{fontSize:11}}/>
            <Bar dataKey={`Bruto ${year}`} fill="#111"/>
            <Bar dataKey={`Bruto ${compare}`} fill="#9ca3af"/>
            <Bar dataKey={`Coste ${year}`} fill="#ef4444"/>
            <Bar dataKey={`Coste ${compare}`} fill="#fca5a5"/>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Neto mensual línea */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <h3 className="font-display text-lg font-black mb-4">Neto mensual {year}</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
            <XAxis dataKey="mes" fontSize={10}/>
            <YAxis fontSize={10} tickFormatter={v=>new Intl.NumberFormat("es-ES",{notation:"compact"}).format(v)}/>
            <Tooltip formatter={v=>eur(v)}/>
            <Line type="monotone" dataKey={`Neto ${year}`} stroke="#10b981" strokeWidth={2} dot={{ r: 3 }}/>
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Histórico anual */}
      {historicoData.length > 1 && (
        <div className="bg-white border rounded-lg p-5 mb-6">
          <h3 className="font-display text-lg font-black mb-4">Histórico anual completo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={historicoData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee"/>
              <XAxis dataKey="year" fontSize={11}/>
              <YAxis fontSize={10} tickFormatter={v=>new Intl.NumberFormat("es-ES",{notation:"compact"}).format(v)}/>
              <Tooltip formatter={v=>eur(v)}/>
              <Legend wrapperStyle={{fontSize:11}}/>
              <Bar dataKey="Bruto" fill="#111"/>
              <Bar dataKey="Neto" fill="#10b981"/>
              <Bar dataKey="Coste" fill="#ef4444"/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Detalle mensual */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h3 className="font-display text-lg font-black">Detalle mensual {year}</h3>
          <span className="text-xs text-[var(--ink-muted)]">Descarga la nómina individual desde el icono <FileText size={11} className="inline"/></span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--bg)] text-xs uppercase text-[var(--ink-muted)] font-mono-num tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Mes</th>
              <th className="text-right px-3 py-2">Bruto</th>
              <th className="text-right px-3 py-2">IRPF</th>
              <th className="text-right px-3 py-2">SS Trab</th>
              <th className="text-right px-3 py-2">Neto</th>
              <th className="text-right px-3 py-2">SS Empr</th>
              <th className="text-right px-3 py-2">Coste empresa</th>
              <th className="text-right px-3 py-2">Δ vs {compare}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.months_current.map((m, i) => {
              const cmp = data.months_compare[i] || {};
              const brutoCmp = cmp.bruto || 0;
              const diff = m.bruto - brutoCmp;
              const pct = brutoCmp ? (diff * 100 / brutoCmp) : null;
              const empty = !m.bruto && !m.neto;
              return (
                <tr key={m.mes} className={`border-t ${empty?"opacity-40":""}`}>
                  <td className="px-3 py-2 font-mono-num text-xs">{MESES[m.mes-1]}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{eur(m.bruto)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-[var(--ink-muted)]">{eur(m.irpf)}</td>
                  <td className="px-3 py-2 text-right font-mono-num text-[var(--ink-muted)]">{eur(m.ss_trabajador)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{eur(m.neto)}</td>
                  <td className="px-3 py-2 text-right font-mono-num">{eur(m.ss_empresa)}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold text-rose-700">{eur(m.coste_empresa)}</td>
                  <td className={`px-3 py-2 text-right font-mono-num text-xs ${pct===null?"text-[var(--ink-muted)]":(pct>=0?"text-emerald-700":"text-rose-700")}`}>
                    {pct===null ? "—" : `${pct>0?"+":""}${pct.toFixed(1)}%`}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {m.pdf_id && m.nomina_id && (
                      <button title="Ver PDF" onClick={()=>downloadPdf(m.nomina_id)} className="p-1 text-[var(--ink-muted)] hover:text-[var(--ink)]" data-testid={`ficha-pdf-${m.mes}`}><FileText size={13}/></button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t-2 border-black bg-[var(--bg)] font-semibold">
              <td className="px-3 py-2 text-xs uppercase tracking-wider">Total {year}</td>
              <td className="px-3 py-2 text-right font-mono-num">{eur(tc.bruto)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{eur(tc.irpf)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{eur(tc.ss_trabajador)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{eur(tc.neto)}</td>
              <td className="px-3 py-2 text-right font-mono-num">{eur(tc.ss_empresa)}</td>
              <td className="px-3 py-2 text-right font-mono-num text-rose-700">{eur(tc.coste_empresa)}</td>
              <td className="px-3 py-2 text-right font-mono-num text-xs">
                {g.bruto===null||g.bruto===undefined ? "—" : `${g.bruto>0?"+":""}${g.bruto.toFixed(1)}%`}
              </td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ label, value, compareValue, compareYear, growth, color }) {
  const colorMap = { emerald: "text-emerald-700", rose: "text-rose-700", ink: "text-[var(--ink)]" };
  const Trend = growth === null || growth === undefined ? Minus : (growth >= 0 ? TrendingUp : TrendingDown);
  const trendColor = growth === null || growth === undefined ? "text-[var(--ink-muted)]" : (growth >= 0 ? "text-emerald-700" : "text-rose-700");
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="text-xs uppercase tracking-wider text-[var(--ink-muted)] font-mono-num">{label}</div>
      <div className={`mt-1 font-mono-num text-2xl font-bold ${colorMap[color]}`}>{value}</div>
      <div className="flex items-center gap-1 mt-1 text-[11px]">
        <Trend size={11} className={trendColor}/>
        {growth !== null && growth !== undefined ? (
          <span className={trendColor}>{growth > 0 ? "+" : ""}{growth.toFixed(1)}%</span>
        ) : <span className="text-[var(--ink-muted)]">—</span>}
        <span className="text-[var(--ink-muted)]">vs {compareYear}: {compareValue}</span>
      </div>
    </div>
  );
}
